import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { notifyAppSettingsUpdated } from '../../../common/signalr/signalrActions';
import { RootEpic } from '../../../store';
import { createEpicFromAsyncAction, createEpicFromAsyncActionEmptyRequest } from '../../../utils';
import { enqueueSnackbar, pricePrgError } from '../../ui/redux/uiSlices';
import {
    fetchAppSettings,
    fetchOrganization,
    fetchPricelistMailingSchedule,
    fetchUserSettings,
    getApplicationRestrictions,
    saveGeneralAppSettings,
    sendPricelistsToCustomers,
    sendPricelistsToEmail,
    updateApplicationRestriction,
    updateOrganization,
    updatePricelistMailingSchedule,
    updateTableSettings
} from './settingsSlices';

const appSettings$ = createEpicFromAsyncActionEmptyRequest(fetchAppSettings, ({ settingsClient }) => {
    return settingsClient.getAppSettings();
});

const userSettings$ = createEpicFromAsyncActionEmptyRequest(fetchUserSettings, ({ userSettingsClient }) =>
    userSettingsClient.getUserSettings()
);

const updateTableSettings$ = createEpicFromAsyncAction(
    updateTableSettings,
    (request, { userSettingsClient }) => userSettingsClient.updateTableSettings(request)
);

const organization$ = createEpicFromAsyncActionEmptyRequest(fetchOrganization, ({ organizationClient }) =>
    organizationClient.getOrganization()
);

const updateOrganization$ = createEpicFromAsyncAction(
    updateOrganization,
    (payload, { organizationClient }) => {
        const blobPromise: Promise<Blob | null> =
            payload.logoUrl != null ? fetch(payload.logoUrl).then(res => res.blob()) : Promise.resolve(null);

        return blobPromise
            .then(blob =>
                organizationClient.updateOrganization(
                    payload.name,
                    payload.address,
                    payload.zipCode,
                    payload.city,
                    payload.phone,
                    payload.email,
                    payload.country,
                    blob == null
                        ? null
                        : {
                              data: blob,
                              fileName: 'organizationlogo'
                          }
                )
            )
            .then(() => payload);
    },
    undefined,
    (_, err) => pricePrgError(err)
);

const fetchPricelistMailingSchedule$ = createEpicFromAsyncActionEmptyRequest(
    fetchPricelistMailingSchedule,
    ({ settingsClient }) => settingsClient.getPricelistMailingSchedule()
);

const updatePricelistMailingSchedule$ = createEpicFromAsyncAction(
    updatePricelistMailingSchedule,
    (payload, { settingsClient }) => settingsClient.savePricelistMailingSchedule(payload).then(() => payload),
    () => notifyAppSettingsUpdated()
);

const sendPricelistsToMail$ = createEpicFromAsyncAction(
    sendPricelistsToEmail,
    (payload, { settingsClient }) =>
        settingsClient.sendPricelistsToMail(
            payload.mailSendoutId,
            payload.email,
            payload.customerIds,
            payload.validAt
        ),
    req =>
        enqueueSnackbar({
            message: 'Pricelist mails has been sent to ' + req.email,
            severity: 'success'
        }),
    (_, failure) => pricePrgError(failure)
);

const sendPricelistsToCustomers$: RootEpic = (action$, state$, { settingsClient }) =>
    action$.pipe(
        filter(sendPricelistsToCustomers.request.match),
        mergeMap(({ payload }) =>
            from(settingsClient.sendPricelistsToCustomers(payload.mailSendoutId, payload.payload)).pipe(
                // The response will be received via SignalR, because the mail sending can take a while
                mergeMap(() => EMPTY),
                catchError(err => of(sendPricelistsToCustomers.failure(err)))
            )
        )
    );

const sendPricelistsToCustomersFailure$: RootEpic = action$ =>
    action$.pipe(
        filter(sendPricelistsToCustomers.failure.match),
        map(a => pricePrgError(a.payload))
    );

const sendPricelistsToCustomersSuccess$: RootEpic = action$ =>
    action$.pipe(
        filter(sendPricelistsToCustomers.success.match),
        map(() =>
            enqueueSnackbar({
                message: 'Pricelist mails successfully sent.',
                severity: 'success'
            })
        )
    );

const getApplicationRestrictions$ = createEpicFromAsyncAction(
    getApplicationRestrictions,
    (_, { applicationSettingsClient }) => applicationSettingsClient.getAll()
);

const updateApplicationRestrictions$ = createEpicFromAsyncAction(
    updateApplicationRestriction,
    (request, { applicationSettingsClient }) => applicationSettingsClient.update(request).then(() => request)
);

const saveGeneralAppSettings$ = createEpicFromAsyncAction(
    saveGeneralAppSettings,
    (request, { settingsClient }) =>
        settingsClient.saveGeneralApplicationSettings(request).then(() => request),
    () => notifyAppSettingsUpdated()
);

export default [
    appSettings$,
    userSettings$,
    updateTableSettings$,
    organization$,
    updateOrganization$,
    fetchPricelistMailingSchedule$,
    updatePricelistMailingSchedule$,
    sendPricelistsToMail$,
    sendPricelistsToCustomers$,
    sendPricelistsToCustomersFailure$,
    sendPricelistsToCustomersSuccess$,
    getApplicationRestrictions$,
    updateApplicationRestrictions$,
    saveGeneralAppSettings$
];
